import type React from "react";

import {
  BUSINESS_NAME,
  EMAIL,
  getFormattedAddress,
} from "@/businessInfo/business";

const LAST_UPDATED = "November 7, 2025";

const TermsOfServicePage: React.FC = () => {
  const mailingAddress = getFormattedAddress();

  return (
    <main className="bg-white py-16">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <header className="space-y-4">
          <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Last updated {LAST_UPDATED}
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
            Terms of Service
          </h1>
          <p className="text-base leading-relaxed text-slate-700">
            These Terms of Service (&quot;Terms&quot;) govern your use of the
            websites, communications, and digital resources offered by{" "}
            {BUSINESS_NAME}. Our mission is advanced in collaboration with
            community partners serving Brightwood and the broader Washington,
            D.C. region. By accessing or using our site, participating in
            programs, registering for events, or donating, you agree to these
            Terms.
          </p>
        </header>

        <section className="mt-12 space-y-8">
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              1. Mission-Aligned Purpose
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              We work to strengthen families, expand access to essential
              resources, and build sustainable community infrastructure. Our
              collaborative partners help deliver food distribution, holiday
              baskets, the Weekend Backpack Program, small business assistance,
              transitional housing, community clean teams, and neighborhood
              facade improvements. Additional partners—including MCT for housing
              counseling, The Community Builders for mixed-use projects, and
              United Methodist Just Neighbors for immigration services—extend
              our impact. These Terms reflect how we steward these collaborative
              efforts online.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              2. Eligibility and Account Responsibilities
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              You must be at least 13 years of age to submit personal
              information through our site without parental consent. If you
              create an account, register for an event, or volunteer, you agree
              to provide accurate information and to update it promptly. You are
              responsible for safeguarding any login credentials and for
              notifying us immediately if you suspect unauthorized access.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              3. Acceptable Use
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              You agree not to misuse our website or digital channels.
              Prohibited conduct includes attempting to gain unauthorized
              access, submitting malicious or defamatory content,
              misrepresenting your affiliation, or interfering with the
              operation of community programs. We may restrict or terminate
              access for violations.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              4. Community Engagement and Program Participation
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              Opportunities to volunteer, donate, or receive services align with
              priority programs described below. Participation may require
              additional eligibility criteria or release forms managed by our
              partners:
            </p>
            <ul className="list-disc space-y-2 pl-6 text-base leading-relaxed text-slate-700">
              <li>
                Food access efforts such as weekly pantry distributions,
                Thanksgiving Baskets, and the Weekend Backpack Program in
                partnership with the Capital Area Food Bank.
              </li>
              <li>
                Housing counseling, homeowner training, and renter assistance
                delivered with MCT, including foreclosure mitigation and
                apartment locating support.
              </li>
              <li>
                Small business consultations, marketing workshops, and grants
                guidance that uplift the Ward 4 Brightwood business corridor.
              </li>
              <li>
                Community revitalization projects such as mixed-use development
                planning, neighborhood stewardship teams, and facade improvement
                grants.
              </li>
              <li>
                Immigration legal support through the United Methodist Just
                Neighbors program and annual events like the Walk for the
                Homeless.
              </li>
            </ul>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              5. Donations and Financial Support
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              When you contribute financially, you authorize us to process the
              transaction through secure third-party payment platforms.
              Donations are non-refundable except where required by law or
              expressly stated. Acknowledgements and tax receipts are provided
              according to current regulations.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              6. Third-Party Services and Links
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              Our site may reference or link to partner organizations such as
              MCT, The Community Builders, or United Methodist Just Neighbors.
              Each partner maintains its own policies. We are not responsible
              for third-party content or actions, and engagement with partner
              services is at your discretion.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              7. Intellectual Property
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              Unless otherwise noted, the content, graphics, and branding on our
              site are owned by {BUSINESS_NAME} or used with permission from
              partners. You may not reuse, reproduce, or distribute materials
              without prior written consent.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              8. Disclaimers and Limitation of Liability
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              Our website and services are provided &quot;as is&quot; without
              warranties of any kind. To the fullest extent permitted by law,
              {BUSINESS_NAME} and our collaborating partners are not liable for
              indirect, incidental, or consequential damages arising from your
              use of the site or participation in programs. Certain
              jurisdictions do not allow limitations on implied warranties, so
              some limitations may not apply to you.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              9. Indemnification
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              You agree to indemnify and hold harmless {BUSINESS_NAME} and our
              officers, directors, staff, volunteers, and partners from any
              claims, damages, or expenses arising out of your breach of these
              Terms or misuse of our services.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              10. Governing Law
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              These Terms are governed by the laws of the District of Columbia
              without regard to conflict-of-law principles. Any disputes will be
              resolved in the state or federal courts located within the
              District of Columbia.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              11. Changes to These Terms
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              We may revise these Terms to reflect evolving programs, regulatory
              requirements, or operational needs. Updated Terms will be posted
              with a revised effective date. Continued use of our website or
              services after changes are posted constitutes acceptance of the
              revised Terms.
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-slate-900">
              12. Contact
            </h2>
            <p className="text-base leading-relaxed text-slate-700">
              For questions about these Terms or to request assistance, contact
              us at {EMAIL} or mail correspondence to {BUSINESS_NAME},{" "}
              {mailingAddress}. We appreciate your partnership as we work to
              build a healthier, economically empowered community.
            </p>
          </div>
        </section>
      </div>
    </main>
  );
};

export default TermsOfServicePage;
